import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { clearParticipantsCourses, loadCoursesByParticipantId } from '../../../store/courseService';
import { selectAttendedStatusId } from '../../../store/dataSelectors';
import {
    loadAttendanceDetails,
    loadBloodTestEvents,
    loadMeasurementTypes
} from '../../../store/directusService';
import { setCurrentlySelectedParticipant } from '../../../store/participantService';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    SELF_EMPLOYED_COACH,
    SUPERUSER
} from '../../../utils/userRoles';
import AlcoholConsumed from '../../participant/wellbeing/alcoholConsumed/AlcoholConsumed';
import BloodGlucose from '../../participant/wellbeing/bloodGlucose/BloodGlucose';
import BloodPressure from '../../participant/wellbeing/bloodPressure/BloodPressure';
import BloodTest from '../../participant/wellbeing/bloodTest/BloodTest';
import BMI from '../../participant/wellbeing/bmi/BMI';
import HealthIncident from '../../participant/wellbeing/healthIncident/HealthIncident';
import PhysicalActivity from '../../participant/wellbeing/physicalActivity/PhysicalActivity';
import SignPosting from '../../participant/wellbeing/signPosting/SignPosting';
import Smoking from '../../participant/wellbeing/smoking/Smoking';
import Steps from '../../participant/wellbeing/steps/Steps';
import T2DRHealthData from '../../participant/wellbeing/t2drHealthData/T2DRHealthData';
import WaistMeasurements from '../../participant/wellbeing/waistMeasurements/WaistMeasurements';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, SUPERUSER];
const createRoles = [ADVISER, MANAGER, QUALITY, SELF_EMPLOYED_COACH, SUPERUSER];
const restrictedEditRoles = [QUALITY, SUPERUSER];
const deleteRoles = [MANAGER, QUALITY, SUPERUSER];

const requireLinkedModuleTypes = [
    't2dr health data',
    'bmi',
    'blood test',
    'health incident',
    'steps'
];
const requireMeasurementTypes = ['bmi', 'blood glucose', 'blood pressure'];
const requireBloodTestEvents = ['bmi', 'blood test'];

const ParticipantSection = ({ participant, healthTypes, moduleId }) => {
    const dispatch = useDispatch();

    const [types, setTypes] = useState([]);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
    const [isCreateDisabled, setIsCreateDisabled] = useState(true);
    const [isRestrictedEditDisabled, setIsRestrictedEditDisabled] = useState(true);
    const [isCoursesRequired, setIsCoursesRequired] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const wellbeingDetails = useSelector(
        (state) => state.entities.directusService.wellbeingDetails
    );
    const measurementTypes = useSelector(
        (state) => state.entities.directusService.measurementTypes
    );
    const bloodTestEvents = useSelector((state) => state.entities.directusService.bloodTestEvents);
    const participantsCourses = useSelector(
        (state) => state.entities.courseService.participantsCourses
    );
    const participantsCoursesMetaData = useSelector(
        (state) => state.entities.courseService.participantsCoursesMetaData
    );
    const loadingParticipantsCourses = useSelector(
        (state) => state.entities.courseService.loadingParticipantsCourses
    );
    const attendedStatusId = useSelector(selectAttendedStatusId);

    // USEEFFECTS
    useEffect(() => {
        dispatch(setCurrentlySelectedParticipant(participant));
        if ('status' in participant) {
            const disableOnClosedStatus =
                participant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
            setIsCreateDisabled(!hasRole(createRoles, roles) || disableOnClosedStatus);
            setIsRestrictedEditDisabled(
                !hasRole(restrictedEditRoles, roles) || disableOnClosedStatus
            );
            setIsDeleteDisabled(!hasRole(deleteRoles, roles) || disableOnClosedStatus);
        }
        return () => cleanUp();
    }, [participant, roles]);

    useEffect(() => {
        if (!wellbeingDetails?.length) return;
        const sections = wellbeingDetails
            .filter((el) => healthTypes.includes(el.name.toLowerCase()))
            .filter((el) =>
                el.contract.find((entry) => entry.contract_id.id === participant.contractId)
            )
            .map((el) => el.name.toLowerCase());
        setTypes(sections);
    }, [wellbeingDetails, healthTypes, participant.contractId]);

    useEffect(() => {
        if (!types.length) return;
        const requireCourses = types.some((el) => requireLinkedModuleTypes.includes(el));
        if (requireCourses) {
            setIsCoursesRequired(true);
            dispatch(loadCoursesByParticipantId(participant.id, 0));
            !attendedStatusId && dispatch(loadAttendanceDetails());
        }

        const requireMeasurements = types.some((el) => requireMeasurementTypes.includes(el));
        if (requireMeasurements && measurementTypes?.length < 1) dispatch(loadMeasurementTypes());

        const requireEvents = types.some((el) => requireBloodTestEvents.includes(el));
        if (requireEvents && bloodTestEvents?.length < 1) dispatch(loadBloodTestEvents());
    }, [types]);

    useEffect(() => {
        if (!isCoursesRequired) return;
        if (
            !loadingParticipantsCourses &&
            participantsCourses.length > 0 &&
            'number' in participantsCoursesMetaData &&
            !participantsCoursesMetaData.last
        ) {
            dispatch(
                loadCoursesByParticipantId(participant.id, participantsCoursesMetaData.number + 1)
            );
        }
    }, [
        isCoursesRequired,
        participantsCoursesMetaData,
        participantsCourses,
        loadingParticipantsCourses
    ]);

    // FNs
    const cleanUp = () => dispatch(clearParticipantsCourses());

    //RENDER
    return (
        <div>
            {types.includes('signposting') && (
                <SignPosting
                    participant={participant}
                    isDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                />
            )}
            {types.includes('bmi') && (
                <BMI
                    participant={participant}
                    linkedModuleId={moduleId}
                    isEditDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                    isCreateDisabled={isCreateDisabled}
                />
            )}
            {types.includes('waist measurement') && (
                <WaistMeasurements
                    participant={participant}
                    isDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                />
            )}
            {types.includes('alcohol consumed') && (
                <AlcoholConsumed
                    participant={participant}
                    isDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                />
            )}
            {types.includes('physical activity') && (
                <PhysicalActivity
                    participant={participant}
                    isDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                />
            )}
            {types.includes('blood test') && (
                <BloodTest
                    participant={participant}
                    linkedModuleId={moduleId}
                    isEditDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                    isCreateDisabled={isCreateDisabled}
                />
            )}
            {types.includes('blood glucose') && (
                <BloodGlucose
                    participant={participant}
                    isEditDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                    isCreateDisabled={isCreateDisabled}
                />
            )}
            {types.includes('blood pressure') && (
                <BloodPressure
                    participant={participant}
                    isEditDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                    isCreateDisabled={isCreateDisabled}
                />
            )}
            {types.includes('health incident') && (
                <HealthIncident
                    participant={participant}
                    linkedModuleId={moduleId}
                    isEditDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                    isCreateDisabled={isCreateDisabled}
                />
            )}
            {types.includes('t2dr health data') && (
                <T2DRHealthData
                    participant={participant}
                    linkedModuleId={moduleId}
                    isCreateDisabled={isCreateDisabled}
                    isEditDisabled={isRestrictedEditDisabled}
                />
            )}
            {types.includes('smoking') && (
                <Smoking
                    participant={participant}
                    isDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                />
            )}
            {types.includes('steps') && (
                <Steps
                    participant={participant}
                    linkedModuleId={moduleId}
                    isEditDisabled={isDisabled}
                    isDeleteDisabled={isDeleteDisabled}
                    isCreateDisabled={isCreateDisabled}
                />
            )}
        </div>
    );
};

ParticipantSection.propTypes = {
    participant: PropTypes.object.isRequired,
    healthTypes: PropTypes.arrayOf(PropTypes.string),
    moduleId: PropTypes.string
};

export default ParticipantSection;
